import React from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useOAD } from '../../layout/OADLayout';
import { buildPath } from '../shared/navConfig';
import { api } from '../shared/api/api';
import { useApiData } from '../shared/hooks/useFetch';
import GraphAssolement3 from '../shared/charts/assolement_graph_3';
import ElevageChart from '../shared/charts/ElevageChart';

// Les raccourcis du frame "Tableau de bord", groupés par niveau 1 : chaque
// groupe est introduit par une bande verte ("Emphase ..." dans la maquette).
// Chaque bouton pointe vers le sous-onglet par défaut de sa destination,
// résolu par buildPath depuis navConfig.
const GROUPES = [
  {
    titre: '2035 - 2050',
    boutons: [
      { label: 'Impacts globaux', cible: ['2035-2050', 'impacts-globaux'] },
      { label: 'Eau et irrigation', cible: ['2035-2050', 'eau-irrigation'] },
      { label: 'Adaptations', cible: ['2035-2050', 'adaptations'] },
    ],
  },
  {
    titre: 'Comprendre - Comparer',
    boutons: [
      { label: 'Comprendre', cible: ['comparer-comprendre', 'comprendre'] },
      { label: 'Me comparer', cible: ['comparer-comprendre', 'comparer'] },
    ],
  },
  {
    titre: '2026 - 2027',
    boutons: [
      { label: 'Eau à 3 mois', cible: ['2026-2027', 'eau-3-mois'] },
      { label: 'Optimisation assurance', cible: ['2026-2027', 'optimisation-assurance'] },
      { label: 'Optimisation assolement', cible: ['2026-2027', 'optimisation-assolement'] },
    ],
  },
];

/**
 * Frame Figma "Tableau de bord" : page d'arrivée après le bouton "Consulter"
 * d'une simulation.
 *
 * Colonne de gauche : raccourcis groupés par niveau 1 (pas de sidebar ici).
 * Contenu : deux blocs, "Grandes cultures et viticultures" (surface et produit
 * brut, repris d'exploitation.js) et "Elevage" (effectifs du cheptel, nouveau
 * graphique alimenté en mock tant que la route n'existe pas).
 */
export default function TableauDeBord() {
  const navigate = useNavigate();
  const { idExploit } = useOAD();

  const parametres = React.useMemo(
    () => ({ idExploitation: idExploit }),
    [idExploit]
  );

  const {
    data: exploitation,
    loading: chargementExploitation,
    error: erreurExploitation,
  } = useApiData(api.getExploitation, parametres);

  const {
    data: elevage,
    loading: chargementElevage,
    error: erreurElevage,
  } = useApiData(api.getElevage, parametres);

  // exploitation.js renvoyait l'utilisateur à l'accueil en purgeant la session
  // quand l'API répond "Exploitaiton <id> non trouvée" (chaîne, pas un objet).
  // On garde ce comportement : sans lui, une session périmée afficherait une
  // page vide au lieu de redemander une connexion.
  const exploitationIntrouvable =
    typeof exploitation === 'string' && exploitation.includes('non trouvée');

  React.useEffect(() => {
    if (exploitationIntrouvable) localStorage.clear();
  }, [exploitationIntrouvable]);

    // page/exploitation renvoie surfaceTotale / PBTotal (l'endpoint
  // page/optimisation, lui, utilise SurfaceTot / PBTot : ce ne sont pas les
  // mêmes champs, les deux pages tapent deux routes différentes).
  const surfaceTotale = Math.round(exploitation?.surfaceTotale ?? 0);
  const produitBrutTotal = exploitation?.PBTotal ?? 0;

  if (exploitationIntrouvable) return <Navigate to="/" replace />;

  return (
    <div className="oad-contenu">
      <nav className="tdb-raccourcis">
        {GROUPES.map((groupe) => (
          <div key={groupe.titre} className="tdb-groupe">
            <div className="tdb-emphase">{groupe.titre}</div>
            {groupe.boutons.map((bouton) => (
              <button
                key={bouton.label}
                type="button"
                className="tdb-raccourci"
                onClick={() =>
                  navigate(buildPath(...bouton.cible), { state: { idExploit } })
                }
              >
                {bouton.label}
              </button>
            ))}
          </div>
        ))}
      </nav>

      <main className="oad-cards">
        <section className="oad-card">
          <h2 className="tdb-section-titre">Grandes cultures et viticultures</h2>

          {exploitation?.libelleRA && (
            <p className="tdb-region">
              Votre région agricole est : <b>{exploitation.libelleRA}</b>
            </p>
          )}

          {erreurExploitation && (
            <p className="tdb-erreur">{erreurExploitation}</p>
          )}

          {chargementExploitation ? (
            <p className="tdb-chargement">Chargement de votre assolement…</p>
          ) : (
            !erreurExploitation && (
              <div className="tdb-deux-graphiques">
                <div className="tdb-graphique">
                  <div className="titrePlus">
                    Surface
                    <span style={{ fontWeight: 'normal', fontSize: '70%' }}>
                      {' '}({surfaceTotale} hectares)
                    </span>
                  </div>
                  <GraphAssolement3
                    myData={exploitation?.dataSurface ?? []}
                    PB={''}
                    print={false}
                  />
                </div>

                <div className="tdb-graphique">
                  <div className="titrePlus">
                    Produit brut
                    <span style={{ fontWeight: 'normal', fontSize: '70%' }}>
                      {' '}({produitBrutTotal.toLocaleString()} euros)
                    </span>
                  </div>
                  <GraphAssolement3
                    myData={exploitation?.dataPB ?? []}
                    PB={''}
                    print={false}
                  />
                </div>
              </div>
            )
          )}
        </section>

        <section className="oad-card">
          <h2 className="tdb-section-titre">Elevage</h2>

          {erreurElevage && <p className="tdb-erreur">{erreurElevage}</p>}

          {chargementElevage ? (
            <p className="tdb-chargement">Chargement des effectifs…</p>
          ) : (
            !erreurElevage && <ElevageChart data={elevage?.categories ?? []} />
          )}
        </section>
      </main>
    </div>
  );
}
